/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {error} from '@ion/core/src/logger/logger';
import {
  createRole,
  deleteRole as destroyRole,
  destroyRoles,
  getApplications,
  getRole,
  loadRoles,
  updateRole
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  EditorMode
} from '@ion/core/src/types/editors';
import {
  AdminRolesFilters,
  AuditChange,
  Entity,
  Role
} from '@ion/core/src/types/entities';
import {
  LoadRolesParams,
  ServiceError
} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {isCloneMode} from '@ion/dls/src/editors/helpers';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState, PageInitializeAction} from '@/types/redux';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns,
  reInitializeState
} from '../../../helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {gridDefaultPageSize} from '../../../utils/helpers';

// name of the reducer
export const NAME                          = 'adminRoles';

// initialize roles ...
export const INITIALIZE_ADMIN_ROLES        = 'INITIALIZE_ADMIN_ROLES';
export const RE_INITIALIZE_ADMIN_ROLES     = 'RE_INITIALIZE_ADMIN_ROLES';

export const SET_GRID_COLUMNS              = 'SET_ADMIN_ROLES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS           = 'UPDATE_ADMIN_ROLES_GRID_COLUMNS';

export const ADMIN_ROLES_GRID_REFRESHED    = 'ADMIN_ROLES_GRID_REFRESHED';

export const SET_VIEW_SUB_GROUPS           = 'SET_ADMIN_ROLES_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE            = 'SET_ADMIN_ROLES_GRID_PAGE_SIZE';

// retrieve roles ...
export const GET_ADMIN_ROLES_REQUEST       = 'GET_ADMIN_ROLES_REQUEST';
export const GET_ADMIN_ROLES_RESPONSE      = 'GET_ADMIN_ROLES_RESPONSE';
export const GET_ADMIN_ROLES_ERROR         = 'GET_ADMIN_ROLES_ERROR';

// role add ...
export const ADD_ADMIN_ROLE                = 'ADD_ADMIN_ROLE';
export const ADD_ADMIN_ROLE_REQUEST        = 'ADD_ADMIN_ROLE_REQUEST';
export const ADD_ADMIN_ROLE_RESPONSE       = 'ADD_ADMIN_ROLE_RESPONSE';
export const ADD_ADMIN_ROLE_ERROR          = 'ADD_ADMIN_ROLE_ERROR';

// role edit ...
export const EDIT_ADMIN_ROLE               = 'EDIT_ADMIN_ROLE';
export const EDIT_ADMIN_ROLE_REQUEST       = 'EDIT_ADMIN_ROLE_REQUEST';
export const EDIT_ADMIN_ROLE_RESPONSE      = 'EDIT_ADMIN_ROLE_RESPONSE';
export const EDIT_ADMIN_ROLE_ERROR         = 'EDIT_ADMIN_ROLE_ERROR';
export const SET_EDIT_LOADING              = 'SET_EDIT_LOADING';

// view role audit log ...
export const AUDIT_ADMIN_ROLE                     = 'AUDIT_ADMIN_ROLE';
export const AUDIT_ADMIN_ROLE_REQUEST             = 'AUDIT_ADMIN_ROLE_REQUEST';
export const AUDIT_ADMIN_ROLE_RESPONSE            = 'AUDIT_ADMIN_ROLE_RESPONSE';
export const AUDIT_ADMIN_ROLE_ERROR               = 'AUDIT_ADMIN_ROLE_ERROR';
export const AUDIT_ADMIN_ROLE_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_ROLE_SHOW_CHANGES_DIALOG';

// role delete ...
export const DELETE_ADMIN_ROLE             = 'DELETE_ADMIN_ROLE';
export const DELETE_ADMIN_ROLE_REQUEST     = 'DELETE_ADMIN_ROLE_REQUEST';
export const DELETE_ADMIN_ROLE_RESPONSE    = 'DELETE_ADMIN_ROLE_RESPONSE';
export const DELETE_ADMIN_ROLE_ERROR       = 'DELETE_ADMIN_ROLE_ERROR';

// bulk role deletes ...
export const DELETE_ADMIN_ROLES            = 'DELETE_ADMIN_ROLES';
export const DELETE_ADMIN_ROLES_REQUEST    = 'DELETE_ADMIN_ROLES_REQUEST';
export const DELETE_ADMIN_ROLES_RESPONSE   = 'DELETE_ADMIN_ROLES_RESPONSE';
export const DELETE_ADMIN_ROLES_ERROR      = 'DELETE_ADMIN_ROLES_ERROR';

export const SET_VIEW_ROLE_STATE           = 'SET_VIEW_ROLE_STATE';

export const SET_SELECTED_ROLES            = 'SET_SELECTED_ROLES';

// admin roles filter model ...
export const SET_ADMIN_ROLES_GRID_FILTER_MODEL = 'SET_ADMIN_ROLES_GRID_FILTER_MODEL';
// search queries
export const UPDATE_ADMIN_ROLES_SEARCH_QUERIES = 'UPDATE_ADMIN_ROLES_SEARCH_QUERIES';
export const RESET_ADMIN_ROLES_SEARCH_QUERIES  = 'RESET_ADMIN_ROLES_SEARCH_QUERIES';

export const initializeAdminRoles = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_ROLES};
};

export const reInitializeAdminRoles = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_ROLES};
};

// Updates the modular search queries for Admin Roles
export const updateAdminRolesSearchQueries = (searchParams: AdminRolesFilters) => ({
  type : UPDATE_ADMIN_ROLES_SEARCH_QUERIES, searchParams
});

// Resets the Roles filters state
export const resetAdminRolesSearchQueries = () => ({
  type : RESET_ADMIN_ROLES_SEARCH_QUERIES
});

export const adminRolesGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_ROLES_GRID_REFRESHED};
};

export const getAdminRoles = ({
  sort,
  filter,
  appKeys,
  pageId = 1,
  pageSize = 20,
  callback: callbackFn,
  includeAppKey = false,
  includeSubGroups = false,
  isExtensions = false,
  isIncludeAppKeys = true
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const accountId = getAccountId(getState);
    dispatch({type: GET_ADMIN_ROLES_REQUEST});

    const {
      adminRoles: {
        search
      }
    } = getState();

    const data: LoadRolesParams = {
      accountId       : accountId,
      applicationKeys : appKeys,
      pageId,
      pageSize,
      includeSubGroups,
      isExtensions
    };

    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    if (includeAppKey) {
      data.includeAppKey = includeAppKey;
    }

    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {applicationKeys} = data;

      if (!applicationKeys && isIncludeAppKeys) {
        const applications   = await getApplications(entityId(accountId)) as any;
        data.applicationKeys = applications?.map(({applicationKey}) => applicationKey).join(',');
      }

      const {roles: items, totalCount} = await loadRoles(data);

      dispatch({type: GET_ADMIN_ROLES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_ROLES_ERROR, error: err});
      return err;
    }
  };
};

/**
 *
 * Invoked when the Add Admin Role editor is to be displayed or removed.
 */
export const addAdminRole = (show = true) => ({
  type : ADD_ADMIN_ROLE, show
});

/**
 * Invoked when a new role is to be created, delegating to the createRole
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addRole = (role: Role, isCtcAdmin: Boolean) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const {editorMode} = getState().adminRoles;
    const isClone      = isCloneMode(editorMode);
    isClone && dispatch({type: SET_EDIT_LOADING, status: true});
    dispatch({type: ADD_ADMIN_ROLE_REQUEST});
    try {
      const roleData = normalizeRole(role, isClone || !isCtcAdmin);
      const created = await createRole(roleData);
      dispatch({type: ADD_ADMIN_ROLE_RESPONSE, role: created, mode: editorMode});
      dispatch(toggleUserPrompt(successAddOptions(Entity.role, isClone)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_ROLE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.role)));
      error(err);
    }
    isClone && dispatch({type: SET_EDIT_LOADING, status: false});
  };
};

/**
 * Invoked when the Edit Admin Role editor is to be displayed or removed.
 */
export const editAdminRole = (role: Role, show = true, editorMode = EditorMode.Hide) => ({
  type : EDIT_ADMIN_ROLE, role, show, mode : editorMode
});

/**
 * Invoked when an existing role is to be updated, delegating to the updateRole
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editRole = (role: Role) => {
  const roleData = normalizeRole(role, true);
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ROLE_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : roleData,
        updateEntity : updateRole,
        getEntity    : getSingleRole,
        onError      : roleUpdateError,
        dispatch
      });
      if (updated) {
        dispatch({type: EDIT_ADMIN_ROLE_RESPONSE, role: updated});
        dispatch(toggleUserPrompt(successEditOptions(Entity.role)));
      }
    } catch (err) {
      roleUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing Role gets resource out of state, delegating to the fetch
 * latest role details successful response or error action accordingly.
 */
const getSingleRole = async(roleID: number) => {
  try {
    const role = await getRole(roleID);
    return {updated: role, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Role gets updated or fetched, CTC API
 * and dispatching a successful or error action accordingly.
 */
const roleUpdateError = (err, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_ROLE_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.role, err)));
  error(err);
};

/**
 * Invoked when the the role auditor is to be displayed or removed.
 */export const auditAdminRole = (role: Role, show = true) => ({
  type : AUDIT_ADMIN_ROLE, role, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_ROLE_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked when an role audit is to be made, delegating to the roleAudit CTC
 * API and dispatching a successful response or error action accordingly.
 */
export const auditRole = (role: Role) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: AUDIT_ADMIN_ROLE_REQUEST});
    try {
      dispatch({type: AUDIT_ADMIN_ROLE_RESPONSE, result: role});
    } catch (err) {
      dispatch({type: AUDIT_ADMIN_ROLE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Delete Admin Role Dialog is to be displayed or removed.
 */
export const deleteAdminRole = (role: Role, show = true) => ({
  type : DELETE_ADMIN_ROLE, role, show
});

/**
 * Invoked when an existing role is to be deleted, delegating to the deleteRole
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteRole = (role: Role) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ROLE_REQUEST});
    try {
      const deleted = await destroyRole(role);
      dispatch({type: DELETE_ADMIN_ROLE_RESPONSE, role: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ROLE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Bulk Delete Admin Roles Dialog is to be displayed or removed.
 */
export const deleteAdminRoles = (roles: Role[], show = true) => ({
  type : DELETE_ADMIN_ROLES, roles, show
});

/**
 * Invoked when a collection of existing roles are to be deleted, delegating to
 * the deleteRoles CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteRoles = (roles: Role[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ROLES_REQUEST});
    try {
      const deleted = await destroyRoles(roles);
      dispatch({type: DELETE_ADMIN_ROLES_RESPONSE, roles: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ROLES_ERROR, error: err});
      error(err);
    }
  };
};

export const setSelectedRoles = (roles: Role[]) =>
  ({type: SET_SELECTED_ROLES, roles});

/**
 * Invoked while Add/Edit Roles and normalize the Role object.
 */
export const normalizeRole = (role, isEdit = false) => {
  let application     = isEdit ? role.application : role.application.value;
  const {_self, name} = application;
  if (_self) {
    _self.title = name;
    application = _self;
  }
  delete role?.accountUI;
  delete role?.applicationUI;
  delete role?.createdOnUI;
  delete role?.inheritableUI;
  delete role?.lastModifiedOnUI;
  return {
    ...role,
    application       : application,
    extendedAttribute : {
      ...role.extendedAttribute,
      application : application
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_ROLES_GRID_FILTER_MODEL, model
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setRolesViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setRolesGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */
export interface AdminRolesEditAction extends AdminAction {
  role?: Role;
  roles?: Role[];
};

interface AdminRolesActionState extends AsyncState {
  selected: Role | Role[] | null;
  result: Role | Role[] | null;
  isEditing: boolean;
  record?: Role | Role[] | null;
};
export interface AdminRolesAuditState extends AdminRolesActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}
export interface AdminRolesState extends PageState {
  roles: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    filterModel: FilterModel | {},
    pageSize: number
  },
  add: AdminRolesActionState,
  edit: AdminRolesActionState,
  manage: AdminRolesActionState,
  audit: AdminRolesAuditState,
  destroy: AdminRolesActionState,
  bulkEdit: AdminRolesActionState,
  bulkDelete: AdminRolesActionState,
  findRoleViewState: any,
  editorMode: EditorMode,
  selectedRoles: {
    roles: Role[]
  },
  search: AdminRolesFilters
};

export interface AdminRolesAction extends AdminAction {
  role?: Role;
  roles?: Role[];
  mode?: EditorMode;
  searchParams?: AdminRolesFilters;
};

// defines the user preference key for the admin roles page ...
export const PREFERENCE = 'admin-roles';

// defines the initial state for the reducer ...
export const initialState: AdminRolesState = {
  isInitialized : false,
  roles         : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    filterModel         : {},
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  manage : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  },
  findRoleViewState : {
    isViewRole : false
  },
  editorMode    : EditorMode.Hide,
  selectedRoles : {
    roles : []
  },
  search : {
    name        : null,
    status      : null,
    application : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_ROLES] : (draft: AdminRolesState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.search.name              = null;
    draft.search.status            = null;
    draft.search.application       = null;
  },
  [RE_INITIALIZE_ADMIN_ROLES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, roles}: AdminRolesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : roles.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_ROLES_GRID_FILTER_MODEL] : ({grid}: AdminRolesState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminRolesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_ROLES_GRID_REFRESHED] : (
    {add, edit, manage, audit, destroy, bulkDelete, bulkEdit}: AdminRolesState
  ) => {
    add.result = null;
    edit.result = null;
    manage.result = null;
    audit.result = null;
    destroy.result = null;
    bulkDelete.result = null;
    bulkEdit.result = null;
  },
  [GET_ADMIN_ROLES_REQUEST] : ({roles}: AdminRolesState) => {
    roles.loading = true;
  },
  [GET_ADMIN_ROLES_RESPONSE] : ({roles}: AdminRolesState) => {
    roles.loading = false;
  },
  [GET_ADMIN_ROLES_ERROR] : ({roles}: AdminRolesState, {error}: AdminRolesAction) => {
    roles.loading = false;
    roles.error   = error;
  },
  [ADD_ADMIN_ROLE] : (draft: AdminRolesState, {show}: AdminRolesAction) => {
    draft.add.isEditing = show;
    draft.add.result    = null;
    draft.editorMode    = EditorMode.Add;
  },
  [ADD_ADMIN_ROLE_REQUEST] : ({add}: AdminRolesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_ROLE_RESPONSE] : ({add, edit}: AdminRolesState, {role, mode}: AdminRolesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = role;
    if (isCloneMode(mode)) {
      edit.selected  = null;
      edit.isEditing = false;
    }
  },
  [ADD_ADMIN_ROLE_ERROR] : ({add}: AdminRolesState, {error}: AdminRolesAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_ROLE] : (draft: AdminRolesState, {role, show, mode}: AdminRolesAction) => {
    draft.edit.selected  = show ? role : null;
    draft.edit.isEditing = show;
    draft.edit.result    = null;
    draft.editorMode     = mode;
  },
  [EDIT_ADMIN_ROLE_REQUEST] : ({edit}: AdminRolesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_ROLE_RESPONSE] : ({edit}: AdminRolesState, {role}: AdminRolesAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = role;
  },
  [EDIT_ADMIN_ROLE_ERROR] : ({edit}: AdminRolesState, {error}: AdminRolesAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [SET_EDIT_LOADING] : ({edit}: AdminRolesState, {status}) => {
    edit.loading = status;
  },
  [AUDIT_ADMIN_ROLE] : ({audit}: AdminRolesState, {role, show}: AdminRolesAction) => {
    audit.selected  = show ? role : null;
    audit.isEditing = show;
    audit.result    = null;
  },
  [AUDIT_ADMIN_ROLE_REQUEST] : ({audit}: AdminRolesState) => {
    audit.loading = true;
    audit.error   = audit.result = null;
  },
  [AUDIT_ADMIN_ROLE_RESPONSE] : ({audit}: AdminRolesState, {result}: AdminRolesAction) => {
    audit.loading   = false;
    audit.isEditing = false;
    audit.result    = result;
  },
  [AUDIT_ADMIN_ROLE_ERROR] : ({audit}: AdminRolesState, {error}: AdminRolesAction) => {
    audit.loading   = false;
    audit.error     = error;
  },
  [AUDIT_ADMIN_ROLE_SHOW_CHANGES_DIALOG] : ({audit}: AdminRolesState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [DELETE_ADMIN_ROLE] : ({destroy}: AdminRolesState, {role, show}: AdminRolesAction) => {
    destroy.selected  = show ? role : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_ROLE_REQUEST] : ({destroy}: AdminRolesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_ROLE_RESPONSE] : ({destroy}: AdminRolesState, {role}: AdminRolesAction) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = role;
  },
  [DELETE_ADMIN_ROLE_ERROR] : ({destroy}: AdminRolesState, {error}: AdminRolesAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [DELETE_ADMIN_ROLES] : ({bulkDelete}: AdminRolesState, {roles, show}: AdminRolesAction) => {
    bulkDelete.selected  = show ? roles : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_ROLES_REQUEST] : ({bulkDelete}: AdminRolesState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_ROLES_RESPONSE] : ({bulkDelete}: AdminRolesState, {roles}: AdminRolesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.isEditing = false;
    bulkDelete.result    = roles;
  },
  [DELETE_ADMIN_ROLES_ERROR] : ({bulkDelete}: AdminRolesState, {error}: AdminRolesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.error     = error;
  },
  [SET_VIEW_ROLE_STATE] : ({findRoleViewState}: AdminRolesState, {allow}) => {
    findRoleViewState.isViewRole = allow;
  },
  [SET_SELECTED_ROLES] : ({selectedRoles}: AdminRolesState, {roles}: AdminRolesAction) => {
    selectedRoles.roles = roles;
  },
  [UPDATE_ADMIN_ROLES_SEARCH_QUERIES] : ({search}: AdminRolesState, {searchParams}) => {
    search.name        = searchParams.name || null;
    search.status      = searchParams.status || null;
    search.application = searchParams.application || null;
  },
  [RESET_ADMIN_ROLES_SEARCH_QUERIES] : ({search}: AdminRolesState) => {
    search.name        = null;
    search.status      = null;
    search.application = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminRolesState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminRolesState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminRolesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
